/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { css as cssClassName } from 'emotion';
import React, { useState } from 'react';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import { FormErrorAlert } from 'components/FormErrorAlert';
import events from 'components/SignupPageContainer/events';
import { trackEvent } from 'modules/analytics';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Button } from 'shared/Button/NewButton';
import { LinkText } from 'shared/Link';
import Text from 'shared/Text';
import { useT } from 'client/i18n/hooks/useT';
import { paths } from 'client/shared/Paths';
import { Step } from './types';

const ImportCheckboxWrapper = styled.div`
  margin-bottom: 32px;
  padding: 10px 20px;
  border: 1px solid #c9cbcd;
  border-radius: 4px;
  background: #ebeced;
`;

const ImportCheckbox = styled(Checkbox)`
  position: relative;
  input {
    width: 16px;
    height: 16px;
    margin-top: 0;
    border: 1px solid #cccdcf;
    border-radius: 4px;
    background-color: #fff;
    -webkit-appearance: none;

    &::before {
      position: absolute;
      top: 1px;
      left: 4px;
      width: 6px;
      height: 10px;
      border: solid #5000b9;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
      visibility: hidden;
      content: '';
    }

    &:checked::before {
      visibility: visible;
    }
  }
`;

const ImportToADifferentAccountButton = styled.button`
  color: #5000b9;
  font-weight: 700;

  &:hover {
    color: #6732b9;
    text-decoration: underline;
    cursor: pointer;
  }
`;

type Props = {
  stationId: string;
  userId: number;
  feedUrl: string;
  previousStep: Step;
  history: {
    push: (url: string) => void;
  };
  logOutAndRedirect?: (redirectPath: string) => void;
  resetPersistedState: () => void;
};

enum ImportStatus {
  LOADING,
  ERROR,
}

export const ImportAgreement = ({
  stationId,
  userId,
  feedUrl,
  previousStep,
  history,
  logOutAndRedirect,
  resetPersistedState,
}: Props) => {
  const t = useT();
  const isFromSignup = previousStep === Step.CREATE_ACCOUNT;
  const [hasAgreed, setHasAgreed] = useState<boolean[]>(
    isFromSignup ? [false] : [false, false]
  );
  const [importStatus, setImportStatus] = useState<ImportStatus>();

  const handleImportClick = async () => {
    try {
      trackEvent(
        'import_podcast_button_clicked',
        {},
        // @ts-ignore
        { providers: [mParticle] }
      );
      setImportStatus(ImportStatus.LOADING);
      await AnchorAPI.importRssFeed({ stationId, userId, feedUrl });

      history.push(`/dashboard${isFromSignup ? '?navigatedFrom=signup' : ''}`);
      resetPersistedState();
    } catch {
      setImportStatus(ImportStatus.ERROR);
    }
  };

  return (
    <div
      css={css`
        max-width: 342px;
      `}
    >
      <ImportCheckboxWrapper>
        {!isFromSignup && (
          <ImportCheckbox
            onChange={() => {
              setHasAgreed(([agreedToOverride, agreedToOwnership]) => [
                !agreedToOverride,
                agreedToOwnership,
              ]);
            }}
          >
            <div
              css={css`
                margin: 0 0 16px 12px;
              `}
            >
              <Text size="md" isBold={true} color="#292f36">
                {t(
                  'ImportOnboarding.ImportAgreement.AccountOverrideMessage',
                  'I understand that importing my podcast will override any existing settings in my Anchor account. Your existing Anchor episodes will not be deleted.',
                  'Import podcast over existing account warning message'
                )}
              </Text>
            </div>
          </ImportCheckbox>
        )}
        <ImportCheckbox
          onChange={() => {
            if (isFromSignup) {
              setHasAgreed(([agreedToOwnership]) => [!agreedToOwnership]);
            } else {
              setHasAgreed(([agreedToOverride, agreedToOwnership]) => [
                agreedToOverride,
                !agreedToOwnership,
              ]);
            }
          }}
        >
          <div
            css={css`
              margin-left: 12px;
            `}
          >
            <Text size="md" isBold={true} color="#292f36">
              {t(
                'ImportOnboarding.ImportAgreement.ImportedPodcastRights',
                'I own, or have licensed, all rights necessary to import this podcast to Anchor, and to grant the license under the {termsOfService}.',
                'Podcast rights acknowledgement message',
                {
                  termsOfService: (
                    <LinkText
                      target="_blank"
                      isInline={true}
                      to={paths.legal.tos}
                    >
                      {t(
                        'Link.TermsOfService',
                        'Terms of Service',
                        'Terms of Service link text'
                      )}
                    </LinkText>
                  ),
                }
              )}
            </Text>
          </div>
        </ImportCheckbox>
      </ImportCheckboxWrapper>
      {importStatus === ImportStatus.ERROR && (
        <FormErrorAlert
          title={t(
            'ImportOnboarding.ImportAgreement.ImportError',
            'Something went wrong',
            'Import podcast error message'
          )}
          renderMessage={t(
            'ImportOnboarding.ImportAgreement.ImportErrorTryAgain',
            'Try again or visit {helpPageLink} for more assistance.',
            'Import podcast error message',
            {
              helpPageLink: (
                <a href={paths.getHelpPage('home')}>
                  {paths.getHelpPage('home')}
                </a>
              ),
            }
          )}
        />
      )}
      <div
        css={css`
          margin-top: 32px;
        `}
      >
        <Button
          color="purple"
          onClick={handleImportClick}
          isDisabled={
            !hasAgreed.every(i => i) || importStatus === ImportStatus.LOADING
          }
          className={cssClassName` width: 100%;color: ${
            hasAgreed ? '#fff' : '#e2cffe'
          };
`}
        >
          {importStatus === ImportStatus.LOADING
            ? t(
                'ImportOnboarding.ImportAgreement.ImportingPodcastButtonLabel',
                'Importing...',
                'Import podcast button text'
              )
            : t(
                'ImportOnboarding.ImportAgreement.ImportPodcastButtonLabel',
                'Import podcast',
                'Import podcast button text'
              )}
        </Button>
      </div>
      {previousStep === Step.ALREADY_LOGGED_IN && (
        <div
          css={css`
            margin-top: 16px;
            text-align: center;
          `}
        >
          <Text size="md" color="#292f36" align="center">
            {t(
              'ImportOnboarding.ImportAgreement.ImportToDifferentAccount',
              'Want to import to a different account?',
              'Import podcast to different account message'
            )}
          </Text>
          {t(
            'ImportOnboarding.ImportAgreement.AccessAcount',
            '{signUpButton} or {loginButton}',
            'Access Anchor account to import podcast',
            {
              signUpButton: (
                <ImportToADifferentAccountButton
                  onClick={() => logOutAndRedirect!('/switch/signup')}
                >
                  {t('Button.Signup', 'Sign up', 'Sign up button text')}
                </ImportToADifferentAccountButton>
              ),
              loginButton: (
                <ImportToADifferentAccountButton
                  onClick={() => logOutAndRedirect!('/switch/login')}
                >
                  {t('Button.Login', 'Log in', 'Log in button text')}
                </ImportToADifferentAccountButton>
              ),
            }
          )}
        </div>
      )}
      <div
        css={css`
          margin-top: 32px;
        `}
      >
        <Text size="sm" color="#7f8287" align="center">
          {t(
            'ImportOnboarding.ImportAgreement.ConditionsMessage',
            'By clicking “Import podcast”, you agree to our {termsOfService} and {privacyPolicy}.',
            'Import podcast conditions agreement',
            {
              termsOfService: (
                <LinkText
                  target="_blank"
                  onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
                  isInline={true}
                  to={paths.legal.tos}
                  color="gray"
                >
                  {t(
                    'Link.TermsOfService',
                    'Terms of Service',
                    'Terms of Service link text'
                  )}
                </LinkText>
              ),
              privacyPolicy: (
                <LinkText
                  target="_blank"
                  onClick={() =>
                    events.signUpLegalLinkClicked({ link: 'privacy' })
                  }
                  isInline={true}
                  color="gray"
                  to={paths.legal.privacy}
                >
                  {t(
                    'Link.PrivacyPolicy',
                    'Privacy Policy',
                    'Privacy Policy link text'
                  )}
                </LinkText>
              ),
            }
          )}
        </Text>
      </div>
    </div>
  );
};
